import React, { memo, useCallback, useEffect, useMemo } from "react";
import { Alert, Box, Grid, Paper, Stack, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { JustInfo } from "../../../utils/Icon";
import FormCSDataFormReferencePanel from "./FormCSDataFormReferencePanel";
import { isFormCSBasisPeriodLongerThan12Months } from "./formCSBasisPeriodUtils";
import { buildFormCSAutoFillValues } from "./formCSFieldReferenceHints";
import {
  FormCSNumberField,
  FormCSDropdownField,
  FormCSTextField,
  FormCSDateField,
  FormCSTaxAdjustmentField,
  FormCSBfDonationField,
  FormCSPlSupplementField,
  FormCSBfCfPairRow,
  FormCSQualificationAmountField,
  FormCSFieldErrorProvider,
  NUMERIC_DROPDOWN_KEYS,
} from "./formCSFieldComponents";
import {
  SCTP_IND_OPTIONS,
  FS_PREP_OPTIONS,
  YES_NO_NUMBER_OPTIONS,
  YES_NO_BLANK_OPTIONS,
  YES_NO_YN_OPTIONS,
  TAX_ADJUSTMENT_GROUPS,
  BF_DONATION_GROUPS,
  PL_SUPPLEMENT_PEOPLE_EXPENSE_KEYS,
  PL_SUPPLEMENT_OPERATING_EXPENSE_KEYS,
  PL_SUPPLEMENT_BALANCE_SHEET_KEYS,
  EIS_EXPENSE_GROUPS,
  EIS_AMOUNT_FIELDS,
  SIGNED_AMOUNT_FIELDS,
  getDataFormCSSectionMeta,
} from "./formCSDataFormFields";

const YES_NO_ONLY_YN_OPTIONS = YES_NO_YN_OPTIONS.filter(
  ({ value }) => value === "Y" || value === "N"
);

const POSITIVE_WHOLE_NUMBER_FIELDS = new Set([
  "sepSrcIncome",
  "receiptNotTxAmt",
  "c1_NTDeductibleExp",
  "renoWorksExpS14Q",
  "c1_FurtherDed",
  "c1_BC",
  "unutilCABFNorm",
  "unutilLossBFNorm",
  "cyCANorm",
  "c1_GrossRent",
  "c1_DedExp",
  "sgIntDisc",
  "sgOtherI",
  "unutilDonationBFNorm",
  "cyDonation",
  "cyOverseasDonation",
  "unutilCACFNorm",
  "unutilLossCFNorm",
  "unutilDonationCFNorm",
]);

const SIGNED_WHOLE_NUMBER_FIELDS = new Set(["profitLossBeforeTaxation"]);

const SECTION_4_TO_8_WHOLE_NUMBER_FIELDS = new Set([
  "totalRevenue",
  "grossPL",
  ...PL_SUPPLEMENT_PEOPLE_EXPENSE_KEYS,
  ...PL_SUPPLEMENT_OPERATING_EXPENSE_KEYS,
  ...PL_SUPPLEMENT_BALANCE_SHEET_KEYS,
  "unutilCALDClaimS23S37",
  "expRD",
  "expRDSG",
  "enhanceDeductRD",
  "c1_EnhancedEISDed",
  ...EIS_AMOUNT_FIELDS,
  "foreignAssetsSaleGainLoss",
  "foreignSourceSaleGainsRemit",
]);

const CALCULATED_FORMULA_FIELDS = new Set([
  "adjPLBefDed",
  "sgAdjPLAft",
  "sgRent",
  "totSgFI",
  "ci",
]);

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const whole = (value) => Math.round(toNumber(value));

const normalizeNumberValue = (field, raw) => {
  if (raw === "" || raw == null) return 0;

  const n = toNumber(raw);
  const value =
    POSITIVE_WHOLE_NUMBER_FIELDS.has(field) ||
    SIGNED_WHOLE_NUMBER_FIELDS.has(field) ||
    SECTION_4_TO_8_WHOLE_NUMBER_FIELDS.has(field)
      ? whole(n)
      : Math.round(n * 100) / 100;

  return SIGNED_AMOUNT_FIELDS.has(field) ? value : Math.max(0, value);
};

const calculateFormCSValues = (data) => {
  const adjPLBefDed =
    whole(data.profitLossBeforeTaxation) -
    whole(data.sepSrcIncome) -
    whole(data.receiptNotTxAmt) +
    whole(data.c1_NTDeductibleExp);
  const sgAdjPLAft =
    adjPLBefDed -
    whole(data.renoWorksExpS14Q) -
    whole(data.c1_FurtherDed) -
    whole(data.c1_EnhancedEISDed);
  const sgRent = Math.max(whole(data.c1_GrossRent) - whole(data.c1_DedExp), 0);
  const rawTotSgFI =
    sgAdjPLAft +
    whole(data.c1_BC) -
    whole(data.unutilCABFNorm) -
    whole(data.unutilLossBFNorm) -
    whole(data.cyCANorm) +
    sgRent +
    whole(data.sgIntDisc) +
    whole(data.sgOtherI);
  const totSgFI = rawTotSgFI;
  const ci =
    totSgFI -
    whole(data.unutilDonationBFNorm) -
    whole(data.cyDonation) -
    whole(data.cyOverseasDonation);

  return {
    ...data,
    adjPLBefDed,
    sgAdjPLAft,
    sgRent,
    totSgFI,
    ci,
  };
};

const hasCalculatedChanges = (next, current) =>
  [...CALCULATED_FORMULA_FIELDS].some(
    (field) => toNumber(next[field]) !== toNumber(current[field])
  );

const canAmountBeNegative = (fieldKey) => SIGNED_AMOUNT_FIELDS.has(fieldKey);

const SectionBlock = memo(function SectionBlock({
  sectionKey,
  title,
  description,
  info,
  children,
}) {
  const sectionMeta = getDataFormCSSectionMeta(sectionKey);
  const resolvedTitle = title ?? sectionMeta.title;
  const resolvedDescription = description ?? sectionMeta.description;
  const resolvedInfo = info ?? sectionMeta.info;

  return (
    <Grid item xs={12}>
      <Paper variant='outlined' sx={{ p: 2 }}>
        <Typography
          variant='subtitle1'
          fontWeight={700}
          sx={{ mb: resolvedDescription ? 0.5 : 1.5 }}
        >
          {resolvedTitle}
          {resolvedInfo && <JustInfo title={resolvedInfo} />}
        </Typography>
        {resolvedDescription && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {resolvedDescription}
          </Typography>
        )}
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Paper>
    </Grid>
  );
});

const SubSectionPanel = memo(function SubSectionPanel({
  sectionKey,
  title,
  description,
  children,
  sx,
  nested = false,
}) {
  const sectionMeta = getDataFormCSSectionMeta(sectionKey);
  const resolvedTitle = title ?? sectionMeta.title;
  const resolvedDescription = description ?? sectionMeta.description;

  const panel = (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: "grey.50",
        borderLeft: "3px solid",
        borderColor: "primary.main",
        ...sx,
      }}
    >
      {resolvedTitle && (
        <Typography
          variant='caption'
          fontWeight={700}
          color='text.secondary'
          sx={{
            display: "block",
            mb: resolvedDescription ? 0.25 : 1.5,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {resolvedTitle}
        </Typography>
      )}
      {resolvedDescription && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          {resolvedDescription}
        </Typography>
      )}
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );

  if (nested) return panel;
  return (
    <Grid item xs={12}>
      {panel}
    </Grid>
  );
});

const FormCSDataFormSection = ({
  dataFormCS,
  onChange,
  taxComputationList,
  selectedTcId,
  onTcChange,
  listLoading,
  selectedTc,
  referenceSummary,
  parsedMainComp,
  plSnapshot,
  mainCompSavedAt,
  assistantLoading,
  assistantError,
  onDownloadPdf,
  pdfGenerating,
  disabled,
  fieldErrors = {},
}) => {
  const updateField = useCallback(
    (field, value) => {
      onChange((prev) => ({ ...prev, [field]: value }));
    },
    [onChange]
  );

  const updateNumber = useCallback(
    (field, raw) => {
      const value = normalizeNumberValue(field, raw);

      onChange((prev) =>
        calculateFormCSValues({
          ...prev,
          [field]: value,
        })
      );
    },
    [onChange]
  );

  useEffect(() => {
    const next = calculateFormCSValues(dataFormCS);
    if (!hasCalculatedChanges(next, dataFormCS)) return;

    onChange((prev) => {
      const updated = calculateFormCSValues(prev);
      return hasCalculatedChanges(updated, prev) ? updated : prev;
    });
  }, [
    dataFormCS.profitLossBeforeTaxation,
    dataFormCS.sepSrcIncome,
    dataFormCS.receiptNotTxAmt,
    dataFormCS.c1_NTDeductibleExp,
    dataFormCS.renoWorksExpS14Q,
    dataFormCS.c1_FurtherDed,
    dataFormCS.c1_BC,
    dataFormCS.unutilCABFNorm,
    dataFormCS.unutilLossBFNorm,
    dataFormCS.cyCANorm,
    dataFormCS.c1_GrossRent,
    dataFormCS.c1_DedExp,
    dataFormCS.sgIntDisc,
    dataFormCS.sgOtherI,
    dataFormCS.unutilDonationBFNorm,
    dataFormCS.cyDonation,
    dataFormCS.cyOverseasDonation,
    dataFormCS.adjPLBefDed,
    dataFormCS.sgAdjPLAft,
    dataFormCS.sgRent,
    dataFormCS.totSgFI,
    dataFormCS.ci,
    dataFormCS,
    onChange,
  ]);

  const updateDropdown = useCallback(
    (field, raw) => {
      const value = NUMERIC_DROPDOWN_KEYS.has(field) ? Number(raw) || 0 : raw;
      onChange((prev) => ({ ...prev, [field]: value }));
    },
    [onChange]
  );

  const isBasisPeriodOver12Months =
    isFormCSBasisPeriodLongerThan12Months(dataFormCS);
  const showSctpFields = dataFormCS.sctpInd === 1;
  const showPrevFye = Number(dataFormCS.fsPrep) === 2;
  const showEisFields =
    dataFormCS.eis_ClaimCashPayout === "1" ||
    dataFormCS.eis_ClaimDedAll === "1";
  const fieldErrorCount = Object.keys(fieldErrors).length;

  const referenceFillValues = useMemo(
    () => buildFormCSAutoFillValues(parsedMainComp, plSnapshot, selectedTc),
    [parsedMainComp, plSnapshot, selectedTc]
  );

  const referenceFillCount = useMemo(
    () => Object.keys(referenceFillValues).length,
    [referenceFillValues]
  );

  const handleFillReference = useCallback(() => {
    if (!referenceFillCount) return;

    onChange((prev) =>
      calculateFormCSValues({
        ...prev,
        ...referenceFillValues,
      })
    );
  }, [onChange, referenceFillCount, referenceFillValues]);

  const taxAdjustmentLeftGroups = useMemo(
    () => TAX_ADJUSTMENT_GROUPS.filter((g) => g.column === "left"),
    []
  );
  const taxAdjustmentRightGroups = useMemo(
    () => TAX_ADJUSTMENT_GROUPS.filter((g) => g.column === "right"),
    []
  );

  return (
    <FormCSFieldErrorProvider fieldErrors={fieldErrors}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <DescriptionIcon color='primary' />
          <Typography variant='h6' fontWeight={600}>
            Form C-S Details
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Complete Form C-S details fields for IRAS. Use the reference panel
          while entering values — tax computation figures are not auto-applied.
        </Typography>

        {fieldErrorCount > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Review {fieldErrorCount} highlighted Form C-S field
            {fieldErrorCount === 1 ? "" : "s"} before continuing.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* <Grid item xs={12}>
          <TitleLabel title='Tax computation (reference source)' />
          {listLoading ? (
            <Stack direction='row' alignItems='center' gap={1} sx={{ py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant='body2' color='text.secondary'>
                Loading tax computations…
              </Typography>
            </Stack>
          ) : taxComputationList.length === 0 ? (
            <Alert severity='info' sx={{ mt: 0.5 }}>
              No saved tax computations. You can still complete Form C-S data
              manually; the reference panel will be empty.
            </Alert>
          ) : (
            <FormCSDropdown
              value={selectedTcId ?? ""}
              options={taxCompDropdownOptions}
              onChange={(e) => onTcChange(e.target.value)}
              disabled={true}
              placeholder='Select tax computation'
            />
          )}
        </Grid> */}
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Box sx={{ flex: "1 1 75%", minWidth: 0 }}>
            <Grid container spacing={2}>
              {/* Module C — Basis period & financial statements */}
              <SectionBlock sectionKey='basisPeriod'>
                <FormCSDateField
                  fieldKey='basisPeriodFrom'
                  value={dataFormCS.basisPeriodFrom}
                  required
                  disabled={disabled}
                  onFieldChange={updateField}
                  xs={12}
                  sm={6}
                />
                <FormCSDateField
                  fieldKey='basisPeriodTo'
                  value={dataFormCS.basisPeriodTo}
                  required
                  disabled={disabled}
                  onFieldChange={updateField}
                  xs={12}
                  sm={6}
                />

                <FormCSDropdownField
                  fieldKey='sctpInd'
                  value={dataFormCS.sctpInd}
                  options={SCTP_IND_OPTIONS}
                  required
                  disabled={disabled}
                  onDropdownChange={updateDropdown}
                  xs={12}
                  sm={12}
                />
                {showSctpFields && (
                  <SubSectionPanel sectionKey='sctpReviewer'>
                    <FormCSTextField
                      fieldKey='sctpName'
                      value={dataFormCS.sctpName}
                      required
                      disabled={disabled}
                      onFieldChange={updateField}
                      xs={12}
                      sm={6}
                    />
                    <FormCSTextField
                      fieldKey='sctpNo'
                      value={dataFormCS.sctpNo}
                      required
                      disabled={disabled}
                      onFieldChange={updateField}
                      xs={12}
                      sm={6}
                    />
                  </SubSectionPanel>
                )}

                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography
                    variant='subtitle2'
                    fontWeight={700}
                    sx={{ mb: 0.5 }}
                  >
                    Financial statements
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1.5 }}
                  >
                    Number of account sets prepared and related financial year
                    ends.
                  </Typography>
                  <Grid container spacing={2}>
                    <FormCSDropdownField
                      fieldKey='fsPrep'
                      value={dataFormCS.fsPrep}
                      options={FS_PREP_OPTIONS}
                      required={isBasisPeriodOver12Months}
                      disabled={disabled}
                      onDropdownChange={updateDropdown}
                      xs={12}
                      sm={12}
                    />
                    <FormCSDateField
                      fieldKey='firstFYE'
                      value={dataFormCS.firstFYE}
                      required={showPrevFye}
                      disabled={disabled}
                      onFieldChange={updateField}
                      xs={12}
                      sm={showPrevFye ? 6 : 12}
                    />
                    {showPrevFye && (
                      <FormCSDateField
                        fieldKey='prevFYE'
                        value={dataFormCS.prevFYE}
                        required
                        disabled={disabled}
                        onFieldChange={updateField}
                        xs={12}
                        sm={6}
                      />
                    )}
                  </Grid>
                </Grid>
              </SectionBlock>

              {/* Module D — Tax adjustments */}
              <SectionBlock sectionKey='taxAdjustments'>
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems='stretch'>
                    <Grid item xs={12} md={5.5}>
                      <Stack spacing={2}>
                        {taxAdjustmentLeftGroups.map((group) => (
                          <SubSectionPanel
                            key={group.sectionKey}
                            nested
                            sectionKey={group.sectionKey}
                          >
                            {group.keys.map((fieldKey) => (
                              <FormCSTaxAdjustmentField
                                key={fieldKey}
                                fieldKey={fieldKey}
                                value={dataFormCS[fieldKey]}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={canAmountBeNegative(fieldKey)}
                                calculated={CALCULATED_FORMULA_FIELDS.has(
                                  fieldKey
                                )}
                              />
                            ))}
                          </SubSectionPanel>
                        ))}
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      md={1}
                      sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                          px: 0.5,
                          py: 2,
                          borderRadius: 1,
                          bgcolor: (theme) => `${theme.palette.primary.main}08`,
                        }}
                      >
                        <ArrowForwardIcon
                          sx={{ color: "primary.main", opacity: 0.45 }}
                        />
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontSize: "0.65rem", textAlign: "center" }}
                        >
                          Next stage
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={5.5}>
                      <Stack spacing={2}>
                        {taxAdjustmentRightGroups.map((group) => (
                          <SubSectionPanel
                            key={group.sectionKey}
                            nested
                            sectionKey={group.sectionKey}
                          >
                            {group.keys.map((fieldKey) => (
                              <FormCSTaxAdjustmentField
                                key={fieldKey}
                                fieldKey={fieldKey}
                                value={dataFormCS[fieldKey]}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={canAmountBeNegative(fieldKey)}
                                calculated={CALCULATED_FORMULA_FIELDS.has(
                                  fieldKey
                                )}
                              />
                            ))}
                          </SubSectionPanel>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </SectionBlock>

              {/* Module E — Brought forward & donations */}
              <SectionBlock sectionKey='bfDonations'>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    {BF_DONATION_GROUPS.filter(
                      (g) => g.variant !== "closing"
                    ).map((group) => (
                      <SubSectionPanel
                        key={group.sectionKey}
                        nested
                        sectionKey={group.sectionKey}
                      >
                        {group.layout === "income" ? (
                          <>
                            <Grid item xs={12}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <Typography
                                    variant='caption'
                                    fontWeight={700}
                                    color='text.secondary'
                                    sx={{ display: "block", mb: 1 }}
                                  >
                                    Opening balances
                                  </Typography>
                                  <Stack spacing={2}>
                                    {[
                                      "unutilCABFNorm",
                                      "cyCANorm",
                                      "unutilLossBFNorm",
                                    ].map((fieldKey) => (
                                      <FormCSBfDonationField
                                        key={fieldKey}
                                        fieldKey={fieldKey}
                                        value={dataFormCS[fieldKey]}
                                        disabled={disabled}
                                        onNumberChange={updateNumber}
                                        xs={12}
                                        sm={12}
                                        md={12}
                                        wholeNumber
                                        allowNegative={false}
                                      />
                                    ))}
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                  <Typography
                                    variant='caption'
                                    fontWeight={700}
                                    color='text.secondary'
                                    sx={{ display: "block", mb: 1 }}
                                  >
                                    Current period income
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {[
                                      "c1_GrossRent",
                                      "c1_DedExp",
                                      "sgIntDisc",
                                      "sgOtherI",
                                    ].map((fieldKey) => (
                                      <FormCSBfDonationField
                                        key={fieldKey}
                                        fieldKey={fieldKey}
                                        value={dataFormCS[fieldKey]}
                                        disabled={disabled}
                                        onNumberChange={updateNumber}
                                        wholeNumber
                                        allowNegative={false}
                                      />
                                    ))}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            {["sgRent", "totSgFI"].map((fieldKey) => (
                              <FormCSBfDonationField
                                key={fieldKey}
                                fieldKey={fieldKey}
                                value={dataFormCS[fieldKey]}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={canAmountBeNegative(fieldKey)}
                                calculated
                              />
                            ))}
                          </>
                        ) : (
                          <>
                            <Grid item xs={24} md={18}>
                              <FormCSBfDonationField
                                fieldKey='unutilDonationBFNorm'
                                value={dataFormCS.unutilDonationBFNorm}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={false}
                                xs={12}
                                sm={12}
                                md={12}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormCSBfDonationField
                                fieldKey='cyDonation'
                                value={dataFormCS.cyDonation}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={false}
                                xs={12}
                                sm={12}
                                md={12}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormCSBfDonationField
                                fieldKey='cyOverseasDonation'
                                value={dataFormCS.cyOverseasDonation}
                                disabled={disabled}
                                onNumberChange={updateNumber}
                                wholeNumber
                                allowNegative={false}
                                xs={12}
                                sm={12}
                                md={12}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <FormCSDropdownField
                                fieldKey='ptisDonInd'
                                value={dataFormCS.ptisDonInd}
                                options={YES_NO_YN_OPTIONS}
                                required
                                disabled={disabled}
                                onDropdownChange={updateDropdown}
                                xs={12}
                                sm={12}
                              />
                            </Grid>

                            <FormCSBfDonationField
                              fieldKey='ci'
                              value={dataFormCS.ci}
                              disabled={disabled}
                              onNumberChange={updateNumber}
                              wholeNumber
                              allowNegative={canAmountBeNegative("ci")}
                              calculated
                              xs={12}
                              sm={12}
                              md={12}
                            />
                          </>
                        )}
                      </SubSectionPanel>
                    ))}

                    {BF_DONATION_GROUPS.filter(
                      (g) => g.variant === "closing"
                    ).map((group) => (
                      <SubSectionPanel
                        key={group.sectionKey}
                        nested
                        sectionKey={group.sectionKey}
                        sx={{
                          bgcolor: "grey.100",
                          borderLeftColor: "grey.500",
                        }}
                      >
                        <FormCSBfCfPairRow
                          bfKey='unutilCABFNorm'
                          cfKey='unutilCACFNorm'
                          rowLabel='Capital allowances'
                          bfValue={dataFormCS.unutilCABFNorm}
                          cfValue={dataFormCS.unutilCACFNorm}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                          bfCalculated
                          wholeNumber
                        />
                        <FormCSBfCfPairRow
                          bfKey='unutilLossBFNorm'
                          cfKey='unutilLossCFNorm'
                          rowLabel='Losses'
                          bfValue={dataFormCS.unutilLossBFNorm}
                          cfValue={dataFormCS.unutilLossCFNorm}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                          bfCalculated
                          wholeNumber
                        />
                        <FormCSBfCfPairRow
                          bfKey='unutilDonationBFNorm'
                          cfKey='unutilDonationCFNorm'
                          rowLabel='Donations'
                          bfValue={dataFormCS.unutilDonationBFNorm}
                          cfValue={dataFormCS.unutilDonationCFNorm}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                          bfCalculated
                          wholeNumber
                        />
                      </SubSectionPanel>
                    ))}
                  </Stack>
                </Grid>
              </SectionBlock>

              {/* Module G — P/L & balance sheet supplement */}
              <SectionBlock sectionKey='plSupplement'>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <SubSectionPanel
                      nested
                      sectionKey='profitOverview'
                      sx={{
                        bgcolor: (theme) => `${theme.palette.primary.main}06`,
                        borderLeftColor: "primary.dark",
                      }}
                    >
                      {["totalRevenue", "grossPL"].map((fieldKey) => (
                        <FormCSPlSupplementField
                          key={fieldKey}
                          fieldKey={fieldKey}
                          value={dataFormCS[fieldKey]}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                          wholeNumber
                          allowNegative={canAmountBeNegative(fieldKey)}
                        />
                      ))}
                    </SubSectionPanel>

                    <SubSectionPanel nested sectionKey='operatingExpenses'>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant='caption'
                              fontWeight={700}
                              color='text.secondary'
                              sx={{ display: "block", mb: 1 }}
                            >
                              People-related
                            </Typography>
                            <Grid container spacing={2}>
                              {PL_SUPPLEMENT_PEOPLE_EXPENSE_KEYS.map(
                                (fieldKey) => (
                                  <FormCSPlSupplementField
                                    key={fieldKey}
                                    fieldKey={fieldKey}
                                    value={dataFormCS[fieldKey]}
                                    disabled={disabled}
                                    onNumberChange={updateNumber}
                                    wholeNumber
                                  />
                                )
                              )}
                            </Grid>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant='caption'
                              fontWeight={700}
                              color='text.secondary'
                              sx={{ display: "block", mb: 1 }}
                            >
                              Operating overheads
                            </Typography>
                            <Grid container spacing={2}>
                              {PL_SUPPLEMENT_OPERATING_EXPENSE_KEYS.map(
                                (fieldKey) => (
                                  <FormCSPlSupplementField
                                    key={fieldKey}
                                    fieldKey={fieldKey}
                                    value={dataFormCS[fieldKey]}
                                    disabled={disabled}
                                    onNumberChange={updateNumber}
                                    wholeNumber
                                  />
                                )
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </SubSectionPanel>

                    <SubSectionPanel
                      nested
                      sectionKey='balanceSheetItems'
                      sx={{
                        bgcolor: "grey.100",
                        borderLeftColor: "grey.500",
                      }}
                    >
                      {PL_SUPPLEMENT_BALANCE_SHEET_KEYS.map((fieldKey) => (
                        <FormCSPlSupplementField
                          key={fieldKey}
                          fieldKey={fieldKey}
                          value={dataFormCS[fieldKey]}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                          wholeNumber
                          sm={6}
                          md={6}
                        />
                      ))}
                    </SubSectionPanel>
                  </Stack>
                </Grid>
              </SectionBlock>

              {/* Module H — Qualification & changes */}
              <SectionBlock sectionKey='qualificationChanges'>
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems='stretch'>
                    <Grid item xs={12} md={7}>
                      <Stack spacing={2} sx={{ height: "100%" }}>
                        <SubSectionPanel nested sectionKey='companyChanges'>
                          <FormCSDropdownField
                            fieldKey='uCALDChangePrinAct'
                            value={dataFormCS.uCALDChangePrinAct}
                            options={YES_NO_ONLY_YN_OPTIONS}
                            required
                            disabled={disabled}
                            onDropdownChange={updateDropdown}
                            xs={12}
                            sm={12}
                          />
                          <FormCSDropdownField
                            fieldKey='sholderChange'
                            value={dataFormCS.sholderChange}
                            options={YES_NO_ONLY_YN_OPTIONS}
                            required
                            disabled={disabled}
                            onDropdownChange={updateDropdown}
                            xs={12}
                            sm={12}
                          />
                        </SubSectionPanel>

                        <SubSectionPanel nested sectionKey='taxExemptions'>
                          <FormCSDropdownField
                            fieldKey='fullTxX'
                            value={dataFormCS.fullTxX}
                            options={YES_NO_ONLY_YN_OPTIONS}
                            required
                            disabled={disabled}
                            onDropdownChange={updateDropdown}
                            xs={12}
                            sm={12}
                          />
                        </SubSectionPanel>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <SubSectionPanel
                        nested
                        sectionKey='amountDeclaration'
                        sx={{
                          height: "100%",
                          bgcolor: (theme) => `${theme.palette.primary.main}06`,
                          borderLeftColor: "primary.dark",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <FormCSQualificationAmountField
                          value={dataFormCS.unutilCALDClaimS23S37}
                          disabled={disabled}
                          onNumberChange={updateNumber}
                        />
                      </SubSectionPanel>
                    </Grid>
                  </Grid>
                </Grid>
              </SectionBlock>

              {/* Module I — R&D */}
              <SectionBlock sectionKey='rd'>
                <Grid item xs={12}>
                  <SubSectionPanel
                    nested
                    sectionKey='researchDevelopment'
                    sx={{
                      bgcolor: (theme) => `${theme.palette.primary.main}04`,
                    }}
                  >
                    <FormCSNumberField
                      fieldKey='expRD'
                      value={dataFormCS.expRD}
                      required
                      disabled={disabled}
                      onNumberChange={updateNumber}
                      xs={12}
                      sm={4}
                      md={4}
                      sgd
                      allowNegative={false}
                      wholeNumber
                    />
                    <FormCSNumberField
                      fieldKey='expRDSG'
                      value={dataFormCS.expRDSG}
                      required
                      disabled={disabled}
                      onNumberChange={updateNumber}
                      xs={12}
                      sm={4}
                      md={4}
                      sgd
                      allowNegative={false}
                      wholeNumber
                    />
                    <FormCSNumberField
                      fieldKey='enhanceDeductRD'
                      value={dataFormCS.enhanceDeductRD}
                      disabled={disabled}
                      onNumberChange={updateNumber}
                      xs={12}
                      sm={4}
                      md={4}
                      sgd
                      allowNegative={false}
                      wholeNumber
                    />
                  </SubSectionPanel>
                </Grid>
              </SectionBlock>

              {/* Module J — Appropriation of trading stock / asset conversion */}
              <SectionBlock sectionKey='stockAppropriation'>
                <Grid item xs={12}>
                  <FormCSDropdownField
                    fieldKey='appStockConvAsset'
                    value={dataFormCS.appStockConvAsset}
                    options={YES_NO_NUMBER_OPTIONS}
                    required
                    disabled={disabled}
                    onDropdownChange={updateDropdown}
                    xs={12}
                    sm={12}
                  />
                </Grid>
              </SectionBlock>

              {/* Module K — Section 10L */}
              <SectionBlock sectionKey='section10L'>
                <Grid item xs={12}>
                  <SubSectionPanel nested sectionKey='foreignAssetDisposal'>
                    <FormCSDropdownField
                      fieldKey='foreignSourceSaleNotTax'
                      value={dataFormCS.foreignSourceSaleNotTax}
                      options={YES_NO_YN_OPTIONS}
                      required
                      disabled={disabled}
                      onDropdownChange={updateDropdown}
                      xs={12}
                      sm={12}
                    />
                    <FormCSNumberField
                      fieldKey='foreignAssetsSaleGainLoss'
                      value={dataFormCS.foreignAssetsSaleGainLoss}
                      required
                      disabled={disabled}
                      onNumberChange={updateNumber}
                      xs={12}
                      sm={6}
                      md={6}
                      sgd
                      allowNegative={canAmountBeNegative(
                        "foreignAssetsSaleGainLoss"
                      )}
                      wholeNumber
                    />
                    <FormCSNumberField
                      fieldKey='foreignSourceSaleGainsRemit'
                      value={dataFormCS.foreignSourceSaleGainsRemit}
                      required
                      disabled={disabled}
                      onNumberChange={updateNumber}
                      xs={12}
                      sm={6}
                      md={6}
                      sgd
                      allowNegative={false}
                      wholeNumber
                    />
                  </SubSectionPanel>
                </Grid>
              </SectionBlock>

              {/* Module L — EIS */}
              <SectionBlock sectionKey='eis'>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <SubSectionPanel nested sectionKey='eisClaimOptions'>
                      <FormCSDropdownField
                        fieldKey='eis_ClaimCashPayout'
                        value={dataFormCS.eis_ClaimCashPayout}
                        options={YES_NO_BLANK_OPTIONS}
                        placeholder='Not Applicable'
                        disabled={disabled}
                        onDropdownChange={updateDropdown}
                        xs={12}
                        sm={6}
                      />
                      <FormCSDropdownField
                        fieldKey='eis_ClaimDedAll'
                        value={dataFormCS.eis_ClaimDedAll}
                        options={YES_NO_BLANK_OPTIONS}
                        placeholder='Not Applicable'
                        disabled={disabled}
                        onDropdownChange={updateDropdown}
                        xs={12}
                        sm={6}
                      />
                    </SubSectionPanel>

                    {showEisFields && (
                      <SubSectionPanel
                        nested
                        sectionKey='eisExpenses'
                        sx={{
                          bgcolor: "grey.50",
                          borderLeftColor: "grey.400",
                          ml: { xs: 0, sm: 1 },
                        }}
                      >
                        {EIS_EXPENSE_GROUPS.map(
                          ({ title, totalCostKey, deductionKey }) => (
                            <Grid item xs={12} key={title}>
                              <Box
                                sx={{
                                  p: 2,
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  bgcolor: "background.paper",
                                }}
                              >
                                <Typography
                                  variant='subtitle2'
                                  fontWeight={700}
                                  sx={{ mb: 1.5 }}
                                >
                                  {title}
                                </Typography>
                                <Grid container spacing={2}>
                                  <FormCSNumberField
                                    fieldKey={totalCostKey}
                                    label='Total qualifying cost incurred'
                                    value={dataFormCS[totalCostKey]}
                                    required
                                    disabled={disabled}
                                    onNumberChange={updateNumber}
                                    xs={12}
                                    sm={6}
                                    md={6}
                                    sgd
                                    allowNegative={false}
                                    wholeNumber
                                  />
                                  <FormCSNumberField
                                    fieldKey={deductionKey}
                                    label='Enhanced deductions / allowances claimed / to be claimed'
                                    value={dataFormCS[deductionKey]}
                                    required
                                    disabled={disabled}
                                    onNumberChange={updateNumber}
                                    xs={12}
                                    sm={6}
                                    md={6}
                                    sgd
                                    allowNegative={false}
                                    wholeNumber
                                  />
                                </Grid>
                              </Box>
                            </Grid>
                          )
                        )}
                      </SubSectionPanel>
                    )}
                  </Stack>
                </Grid>
              </SectionBlock>
            </Grid>
          </Box>

          <Box
            sx={{
              flex: "1 1 25%",
              minWidth: { xs: "100%", md: 220 },
              position: { md: "sticky" },
              top: { md: 8 },
              alignSelf: "flex-start",
            }}
          >
            <FormCSDataFormReferencePanel
              selectedTc={selectedTc}
              referenceSummary={referenceSummary}
              parsedMainComp={parsedMainComp}
              plSnapshot={plSnapshot}
              mainCompSavedAt={mainCompSavedAt}
              assistantLoading={assistantLoading}
              assistantError={assistantError}
              onDownloadPdf={onDownloadPdf}
              onFillReference={handleFillReference}
              fillReferenceCount={referenceFillCount}
              pdfGenerating={pdfGenerating}
              disabled={disabled}
            />
          </Box>
        </Box>
      </Box>
    </FormCSFieldErrorProvider>
  );
};

export default memo(FormCSDataFormSection);
